import {
  Button,
  CheckSelectField,
  HookedForm,
  InputField,
  TextField,
  Tooltip,
} from '@app/components';
import SettingsIcon from '@app/assets/icons/settings-without-background.svg?react';
import InfoIcon from '@app/assets/icons/info.svg?react';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';

import { useFormChangeDetection } from './useFormChangeDetection';

// Define expected form fields to ensure consistent structure
interface CampaignFormData {
  campaignName?: string;
  description?: string;
  platforms?: string[];
  businessName?: string;
  businessAddress?: string;
  businessEmail?: string;
  type?: string;
  selectedCustomers?: string[];
  selectAll?: boolean;
  emailTemplate?: string;
  textTemplate?: string;
  sendImmediately?: boolean;
  initialSendDate?: string;
  sendFollowUp?: boolean;
  followUpDays0?: string;
  followUpPeriod0?: string;
  followUpDays1?: string;
  followUpPeriod1?: string;
  followUpDays2?: string;
  followUpPeriod2?: string;
  useAi?: boolean;
}

export const CreateCampaignSettings = ({
  form,
}: {
  form: UseFormReturn<FieldValues, any, undefined>;
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const formData = useAppSelector((state) => state.campaignForm.formData);
  const selectedBusiness = useAppSelector((state) => state.business.selected);
  const [showValidations, _] = useState(false);

  // Use the custom hook for form change detection
  useFormChangeDetection(form);

  // Helper function to clean and structure form data
  const processFormData = (data: FieldValues): CampaignFormData => {
    const cleanedData: CampaignFormData = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined) {
        cleanedData[key as keyof CampaignFormData] = value;
      }
    });
    return cleanedData;
  };

  const createValidationHandler = (fieldName: string) => async () => {
    const isValid = await form.trigger(fieldName);
    if (isValid) form.clearErrors(fieldName);
  };

  // Register form fields with validation
  useEffect(() => {
    form.register('campaignName', {
      required: 'Campaign name is required',
      onChange: createValidationHandler('campaignName'),
    });
    form.register('description');
    form.register('platforms', {
      required: 'Please select at least one platform',
      onChange: createValidationHandler('platforms'),
    });
    form.register('businessName', {
      required: 'Business name is required',
      onChange: createValidationHandler('businessName'),
    });
    form.register('businessAddress');
    form.register('businessEmail', {
      required: 'Business email is required',
      pattern: {
        value: /\S+@\S+\.\S+/,
        message: 'Invalid email address',
      },
      onChange: createValidationHandler('businessEmail'),
    });
  }, [showValidations, form]);

  // Load form data and set up tracking
  useEffect(() => {
    // First check localStorage for saved form data
    const storedFormData = localStorage.getItem('campaignFormData');
    if (storedFormData) {
      try {
        const parsedData = JSON.parse(storedFormData) as CampaignFormData;
        Object.keys(parsedData).forEach((key) => {
          const typedKey = key as keyof CampaignFormData;
          if (parsedData[typedKey] !== undefined) {
            form.setValue(key, parsedData[typedKey]);
          }
        });
      } catch (error) {
        localStorage.removeItem('campaignFormData');
      }
    }
    // Then check Redux state if localStorage didn't have data
    else if (formData) {
      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined) {
          form.setValue(key, formData[key]);
        }
      });
    }

    // Set business info if available
    if (selectedBusiness && !form.getValues('businessName')) {
      form.setValue('businessName', selectedBusiness.name);
      form.setValue(
        'businessAddress',
        (selectedBusiness as unknown as { address: string }).address
      );
    }

    // Track form changes and save to Redux/localStorage
    const subscription = form.watch((value) => {
      const processedData = processFormData(value);
      dispatch(updateFormData(processedData));
      try {
        localStorage.setItem('campaignFormData', JSON.stringify(processedData));
      } catch (error) {
        // Silent fail for localStorage errors
      }
    });

    return () => subscription.unsubscribe();
  }, [form, formData, selectedBusiness, dispatch]);


  // Custom label component with info icon
  const LabelWithInfo = ({ label, info }: { label: string; info: string }) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-sm font-semibold text-base-davyGray">{label}</span>
      <Tooltip content={info}>
        <InfoIcon className="w-4 h-4 cursor-help" />
      </Tooltip>
    </div>
  );

  return (
    <div className="container mx-auto p-4 flex flex-col justify-between items-center gap-4">
      <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
        <div>
          <h4 className="text-sm font-semibold">Campaign information</h4>
          <p className="text-xs text-base-coolGray">
            Provide general information about this new campaign
          </p>
        </div>
        <hr />
        <HookedForm formHook={form} className="space-y-4">
          <InputField
            name="campaignName"
            label="Campaign name"
            onChange={(e) => {
              form.setValue('campaignName', e.target.value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
            }}
          />
          <TextField name="description" label="Description" placeholder="Description about automation" />
        </HookedForm>
      </div>
      <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
        <div>
          <h4 className="text-sm font-semibold">Send campaign via</h4>
          <p className="text-xs text-base-coolGray">
          Select a channel to send your campaign
          </p>
        </div>
        <hr />
        <HookedForm formHook={form}>
          <CheckSelectField
            placeholder="Email and text"
            name="platforms"
            options={[
              { label: 'Email', value: 'email' },
              { label: 'Text', value: 'sms' },
            ]}
          />
        </HookedForm>
      </div>
      <div className="bg-white w-full max-w-[800px] p-4 space-y-4">
        <div className="flex justify-between items-start">
          <div>
            <h4 className="text-sm font-semibold">Sender</h4>
            <p className="text-xs text-base-coolGray">
              Fill in the sender's details
            </p>
          </div>
          <Button
            type="button"
            kind="secondary"
            onClick={() => navigate('/settings')}
            rightNode={<SettingsIcon className="w-4 h-4" />}
            className="text-sm"
          >
            Edit in business settings
          </Button>
        </div>
        <hr />
        <HookedForm formHook={form} className="space-y-4">
          <div>
            <LabelWithInfo 
              label="Business name" 
              info="The name of your business that will appear as the sender"
            />
            <InputField
              name="businessName"
              label=""
              defaultValue={selectedBusiness?.name}
              disabled={!!selectedBusiness}
              className='disabled:opacity-50'
            />
          </div>
          <div>
            <LabelWithInfo 
              label="Business address" 
              info="Your business address for compliance and customer trust"
            />
            <InputField
              name="businessAddress"
              label=""
              defaultValue={
                (selectedBusiness as unknown as { address: string })?.address
              }
            />
          </div>
          <hr />
          <div>
            <LabelWithInfo 
              label="Business email" 
              info="Email address that customers can use to contact your business"
            />
            <InputField name="businessEmail" label="" />
          </div>
          <div>
            <LabelWithInfo 
              label="EchoRatings phone number" 
              info="Insert your EchoRatings phone number"
            />
            <InputField name="echoRatingsPhoneNumber" label="" type="number" />
          </div>
        </HookedForm>
      </div>


    </div>
  );
};
